/**
 * HireBroker display currency
 * Canonical storage currency: UGX.
 * Display currency: visitor country default, or a saved user choice.
 * Conversion uses live USD-based rates (units of currency per 1 USD).
 */

export const STORAGE_CURRENCY = 'UGX';
export const STORAGE_KEY = 'hirebroker_display_currency';
export const MANUAL_KEY = 'hirebroker_currency_manual';
export const RATE_STORAGE_KEY = 'hirebroker_fx_rates';
export const GEO_PROMPT_KEY = 'hirebroker_geo_prompt';
export const DEFAULT_USD_TO_UGX = 3700;

export const ZERO_DECIMAL = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG',
  'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

export const CURRENCIES = {
  USD: { code: 'USD', name: 'US Dollar', symbol: '$', locale: 'en-US' },
  EUR: { code: 'EUR', name: 'Euro', symbol: '€', locale: 'de-DE' },
  GBP: { code: 'GBP', name: 'British Pound', symbol: '£', locale: 'en-GB' },
  UGX: { code: 'UGX', name: 'Ugandan Shilling', symbol: 'UGX', locale: 'en-UG' },
  KES: { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', locale: 'en-KE' },
  TZS: { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh', locale: 'en-TZ' },
  RWF: { code: 'RWF', name: 'Rwandan Franc', symbol: 'FRw', locale: 'en-RW' },
  NGN: { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', locale: 'en-NG' },
  GHS: { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵', locale: 'en-GH' },
  ZAR: { code: 'ZAR', name: 'South African Rand', symbol: 'R', locale: 'en-ZA' },
  ZMW: { code: 'ZMW', name: 'Zambian Kwacha', symbol: 'ZK', locale: 'en-ZM' },
  MWK: { code: 'MWK', name: 'Malawian Kwacha', symbol: 'MK', locale: 'en-MW' },
  BIF: { code: 'BIF', name: 'Burundian Franc', symbol: 'FBu', locale: 'fr-BI' },
  ETB: { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br', locale: 'am-ET' },
  EGP: { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£', locale: 'ar-EG' },
  MAD: { code: 'MAD', name: 'Moroccan Dirham', symbol: 'MAD', locale: 'ar-MA' },
  XOF: { code: 'XOF', name: 'West African CFA', symbol: 'CFA', locale: 'fr-SN' },
  XAF: { code: 'XAF', name: 'Central African CFA', symbol: 'FCFA', locale: 'fr-CM' },
  CAD: { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', locale: 'en-CA' },
  AUD: { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', locale: 'en-AU' },
  NZD: { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', locale: 'en-NZ' },
  INR: { code: 'INR', name: 'Indian Rupee', symbol: '₹', locale: 'en-IN' },
  AED: { code: 'AED', name: 'UAE Dirham', symbol: 'AED', locale: 'ar-AE' },
  SAR: { code: 'SAR', name: 'Saudi Riyal', symbol: 'SAR', locale: 'ar-SA' },
  QAR: { code: 'QAR', name: 'Qatari Riyal', symbol: 'QAR', locale: 'ar-QA' },
  JPY: { code: 'JPY', name: 'Japanese Yen', symbol: '¥', locale: 'ja-JP' },
  CNY: { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', locale: 'zh-CN' },
  HKD: { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', locale: 'zh-HK' },
  SGD: { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', locale: 'en-SG' },
  KRW: { code: 'KRW', name: 'South Korean Won', symbol: '₩', locale: 'ko-KR' },
  CHF: { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', locale: 'de-CH' },
  SEK: { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', locale: 'sv-SE' },
  NOK: { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', locale: 'nb-NO' },
  DKK: { code: 'DKK', name: 'Danish Krone', symbol: 'kr', locale: 'da-DK' },
  PLN: { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', locale: 'pl-PL' },
  TRY: { code: 'TRY', name: 'Turkish Lira', symbol: '₺', locale: 'tr-TR' },
  BRL: { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', locale: 'pt-BR' },
  MXN: { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', locale: 'es-MX' },
  PHP: { code: 'PHP', name: 'Philippine Peso', symbol: '₱', locale: 'en-PH' },
  IDR: { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', locale: 'id-ID' },
  MYR: { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', locale: 'ms-MY' },
  THB: { code: 'THB', name: 'Thai Baht', symbol: '฿', locale: 'th-TH' },
  VND: { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', locale: 'vi-VN' },
  PKR: { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨', locale: 'en-PK' },
  BDT: { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳', locale: 'bn-BD' },
  ILS: { code: 'ILS', name: 'Israeli Shekel', symbol: '₪', locale: 'he-IL' },
};

export const POPULAR_CODES = [
  'USD', 'EUR', 'GBP', 'UGX', 'KES', 'TZS', 'RWF', 'NGN', 'GHS', 'ZAR',
  'CAD', 'AUD', 'INR', 'AED', 'JPY', 'CNY',
];

export const CURRENCY_CODES = [
  ...POPULAR_CODES,
  ...Object.keys(CURRENCIES).filter((c) => !POPULAR_CODES.includes(c)).sort(),
];

export const COUNTRY_CURRENCY = {
  UG: 'UGX', KE: 'KES', TZ: 'TZS', RW: 'RWF', NG: 'NGN', GH: 'GHS', ZA: 'ZAR',
  ZM: 'ZMW', MW: 'MWK', BI: 'BIF', ET: 'ETB', EG: 'EGP', MA: 'MAD',
  US: 'USD', GB: 'GBP', CA: 'CAD', AU: 'AUD', NZ: 'NZD', IN: 'INR',
  AE: 'AED', SA: 'SAR', QA: 'QAR', JP: 'JPY', CN: 'CNY', HK: 'HKD',
  SG: 'SGD', KR: 'KRW', CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK',
  PL: 'PLN', TR: 'TRY', BR: 'BRL', MX: 'MXN', PH: 'PHP', ID: 'IDR',
  MY: 'MYR', TH: 'THB', VN: 'VND', PK: 'PKR', BD: 'BDT', IL: 'ILS',
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR',
  AT: 'EUR', IE: 'EUR', PT: 'EUR', FI: 'EUR', GR: 'EUR',
  SN: 'XOF', CI: 'XOF', CM: 'XAF', CD: 'CDF', SS: 'SSP',
};

const FALLBACK_RATES = {
  USD: 1,
  UGX: DEFAULT_USD_TO_UGX,
  EUR: 0.92,
  GBP: 0.78,
  KES: 129,
  TZS: 2600,
  RWF: 1380,
  NGN: 1600,
  GHS: 15.5,
  ZAR: 18.2,
  CAD: 1.36,
  AUD: 1.52,
  INR: 83.5,
  AED: 3.67,
  JPY: 150,
  CNY: 7.2,
};

export function decimalsFor(code) {
  return ZERO_DECIMAL.has(String(code).toUpperCase()) ? 0 : 2;
}

export function isManualCurrency() {
  try {
    return localStorage.getItem(MANUAL_KEY) === '1';
  } catch {
    return false;
  }
}

export function getStoredCurrency() {
  try {
    const code = localStorage.getItem(STORAGE_KEY);
    if (code && CURRENCIES[code]) return code;
  } catch {
    /* ignore */
  }
  return null;
}

export function storeCurrency(code, { manual = true } = {}) {
  try {
    if (!CURRENCIES[code]) return;
    localStorage.setItem(STORAGE_KEY, code);
    if (manual) localStorage.setItem(MANUAL_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function getGeoPromptState() {
  try {
    const v = localStorage.getItem(GEO_PROMPT_KEY);
    if (v === 'allowed' || v === 'denied' || v === 'dismissed') return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function setGeoPromptState(state) {
  try {
    if (state) localStorage.setItem(GEO_PROMPT_KEY, state);
  } catch {
    /* ignore */
  }
}

export function currencyForCountry(countryCode) {
  const code = String(countryCode || '').toUpperCase();
  const mapped = COUNTRY_CURRENCY[code];
  if (mapped && CURRENCIES[mapped]) return mapped;
  return null;
}

export function getCachedRates() {
  try {
    const raw = localStorage.getItem(RATE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.rates?.USD || !parsed?.rates?.UGX) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function cacheRates(payload) {
  try {
    localStorage.setItem(RATE_STORAGE_KEY, JSON.stringify({
      rates: payload.rates,
      updated_at: payload.updated_at || new Date().toISOString(),
      source: payload.source || 'live',
    }));
  } catch {
    /* ignore */
  }
}

/** 1 USD in UGX — kept for older UI copy. */
export function getUsdToUgxRate(rates) {
  const r = rates || getCachedRates()?.rates || FALLBACK_RATES;
  const n = Number(r.UGX);
  return n > 0 ? n : DEFAULT_USD_TO_UGX;
}

export function setUsdToUgxRate() {
  /* rates now come from live FX; no-op kept for compatibility */
}

function normalizeRates(fourth) {
  if (fourth && typeof fourth === 'object' && !Array.isArray(fourth)) {
    if (fourth.UGX || fourth.USD) return fourth;
    if (fourth.rates) return fourth.rates;
  }
  if (typeof fourth === 'number' && fourth > 0) {
    return { ...FALLBACK_RATES, UGX: fourth, USD: 1 };
  }
  return getCachedRates()?.rates || FALLBACK_RATES;
}

/**
 * Convert using USD as the pivot. Rates are units of currency per 1 USD.
 */
export function convertAmount(amount, from, to, ratesOrUsdToUgx) {
  const n = Number(amount);
  if (amount == null || amount === '' || Number.isNaN(n)) return null;
  const src = String(from || STORAGE_CURRENCY).toUpperCase();
  const dest = String(to || STORAGE_CURRENCY).toUpperCase();
  if (src === dest) return n;

  const rates = normalizeRates(ratesOrUsdToUgx);
  const fromRate = Number(rates[src]);
  const toRate = Number(rates[dest]);
  if (!(fromRate > 0) || !(toRate > 0)) return n;

  const usd = n / fromRate;
  return usd * toRate;
}

export function formatMoney(
  amount,
  {
    sourceCurrency = STORAGE_CURRENCY,
    displayCurrency = STORAGE_CURRENCY,
    usdToUgx,
    rates,
    compact = false,
  } = {}
) {
  const converted = convertAmount(
    amount,
    sourceCurrency,
    displayCurrency,
    rates || usdToUgx
  );
  if (converted == null) return 'Price on request';

  const meta = CURRENCIES[displayCurrency] || CURRENCIES.UGX;
  const decimals = decimalsFor(displayCurrency);
  const value = decimals === 0 ? Math.round(converted) : Number(converted);

  try {
    if (compact && value >= 1_000_000) {
      return `${meta.symbol} ${(value / 1_000_000).toFixed(1)}M`;
    }
    if (compact && value >= 1000 && decimals > 0) {
      return `${meta.symbol}${value >= 10000 ? Math.round(value / 1000) : (value / 1000).toFixed(1)}k`;
    }

    const formatted = new Intl.NumberFormat(meta.locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);

    if (['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'INR'].includes(displayCurrency)) {
      return `${meta.symbol}${formatted}`;
    }
    return `${meta.symbol} ${formatted}`;
  } catch {
    return `${meta.symbol} ${value}`;
  }
}

export function toStorageUgx(amount, entryCurrency = STORAGE_CURRENCY, usdToUgxOrRates) {
  const converted = convertAmount(amount, entryCurrency, STORAGE_CURRENCY, usdToUgxOrRates);
  if (converted == null) return null;
  return Math.round(converted);
}

export function currencyFromLocale() {
  try {
    const lang = (navigator.language || '').split('-');
    const region = (lang[1] || '').toUpperCase();
    const mapped = currencyForCountry(region);
    if (mapped) {
      return {
        country_code: region,
        currency: mapped,
        source: 'locale',
      };
    }
  } catch {
    /* ignore */
  }
  return { country_code: 'UG', currency: 'UGX', source: 'fallback' };
}

function locateUrls() {
  const urls = ['https://app.hirebroker.co/api/currency/locate'];
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      urls.unshift('http://127.0.0.1:8000/api/currency/locate');
    }
  }
  return urls;
}

export async function locateCountryFromCoords(lat, lng) {
  const params = `lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`;
  for (const base of locateUrls()) {
    try {
      const res = await fetch(`${base}?${params}`, { headers: { Accept: 'application/json' } });
      if (!res.ok) continue;
      const json = await res.json();
      const geo = json?.detected;
      if (geo?.country_code) {
        const currency = currencyForCountry(geo.country_code) || (CURRENCIES[geo.currency] ? geo.currency : null);
        if (currency) {
          return {
            country_code: String(geo.country_code).toUpperCase(),
            country: geo.country,
            currency,
            source: 'gps',
          };
        }
      }
    } catch {
      /* try next */
    }
  }

  try {
    const res = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${encodeURIComponent(lat)}&longitude=${encodeURIComponent(lng)}&localityLanguage=en`
    );
    if (res.ok) {
      const json = await res.json();
      const code = String(json.countryCode || '').toUpperCase();
      const currency = currencyForCountry(code);
      if (currency) {
        return {
          country_code: code,
          country: json.countryName || code,
          currency,
          source: 'gps',
        };
      }
    }
  } catch {
    /* ignore */
  }

  return null;
}

export { FALLBACK_RATES };
